import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  Text,
  Heading,
  TableContainer,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  Flex,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import * as XLSX from "xlsx";
import { appRequest } from "routes";
import ApprovalTimelineTab from "./TimeLineReport";

interface NormalizedWorkflowData {
  workflowName: string;
  pending: number;
  approved: number;
  rejected: number;
}

const WorkUserReports: React.FC = () => {
  const [workflowData, setWorkflowData] = useState<NormalizedWorkflowData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const normalizeData = (rawData: any[]): NormalizedWorkflowData[] => {
    return rawData.map((item) => {
      const normalized: any = {
        workflowName: item.workflowName || "Unnamed Workflow",
        pending: 0,
        approved: 0,
        rejected: 0,
      };

      Object.values(item).forEach((val: any) => {
        if (typeof val === "object" && val._id && val.count !== undefined) {
          const status = val._id.status?.toLowerCase();
          if (["pending", "approved", "rejected"].includes(status)) {
            normalized[status as keyof NormalizedWorkflowData] = val.count;
          }
        }
      });

      return normalized;
    });
  };

  const fetchWorkflowData = async () => {
    try {
      setLoading(true);
      const response: any = await appRequest(
        "document",
        "getWorkflowDocumentCount",
        {}
      );
      const clean = normalizeData(response?.data || []);
      setWorkflowData(clean);
    } catch (error) {
      console.error("Failed to fetch workflow data", error);
      setWorkflowData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflowData();
    searchRef.current?.focus();
  }, []);

  const filteredData = useMemo(() => {
    const trimmed = searchText.trim().toLowerCase();
    return workflowData.filter((wf) =>
      wf.workflowName.toLowerCase().includes(trimmed)
    );
  }, [searchText, workflowData]);

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, wf) => {
        acc.pending += wf.pending;
        acc.approved += wf.approved;
        acc.rejected += wf.rejected;
        return acc;
      },
      { pending: 0, approved: 0, rejected: 0 }
    );
  }, [filteredData]);

  const getBadge = (
    count: number,
    status: "pending" | "approved" | "rejected"
  ) => {
    const colorScheme =
      status === "approved"
        ? "green"
        : status === "pending"
        ? "orange"
        : "red";

    return (
      <Badge
        colorScheme={colorScheme}
        px={3}
        py={1}
        borderRadius="md"
        fontSize="sm"
      >
        {count}
      </Badge>
    );
  };

  const handleDownloadExcel = () => {
    const dataForExcel = filteredData.map((row) => ({
      "Workflow Name": row.workflowName,
      Pending: row.pending,
      Approved: row.approved,
      Rejected: row.rejected,
    }));

    // Add totals
    dataForExcel.push({
      "Workflow Name": "Total",
      Pending: totals.pending,
      Approved: totals.approved,
      Rejected: totals.rejected,
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Workflow Report");

    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ];
    XLSX.writeFile(workbook, "Workflow_Report.xlsx");
  };

  return (
    <Tabs variant="enclosed" colorScheme="blue">
      <TabList>
        <Tab>Workflow Report</Tab>
        <Tab>Vendor Approval Summary</Tab>
      </TabList>

      <TabPanels>
        {/* First Tab (Your Report) */}
        <TabPanel>
          <Box
            p={6}
            bg="white"
            borderWidth="1px"
            borderRadius="xl"
            shadow="md"
            mb={8}
          >
            <Flex
              justify="space-between"
              align="center"
              mb={4}
              flexWrap="wrap"
              gap={4}
            >
              <Heading size="md">📊 Workflow Document Report</Heading>
              <Flex gap={3}>
                <InputGroup w={["100%", "250px"]}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    ref={searchRef}
                    placeholder="Search workflow..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    bg="gray.50"
                    rounded="md"
                  />
                </InputGroup>
                <Button
                  leftIcon={<DownloadIcon />}
                  colorScheme="blue"
                  onClick={handleDownloadExcel}
                >
                  Download
                </Button>
              </Flex>
            </Flex>

            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Workflow Name</Th>
                    <Th isNumeric>Pending</Th>
                    <Th isNumeric>Approved</Th>
                    <Th isNumeric>Rejected</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td colSpan={4}>
                        <Center py={8}>
                          <Spinner size="lg" />
                        </Center>
                      </Td>
                    </Tr>
                  ) : filteredData.length === 0 ? (
                    <Tr>
                      <Td colSpan={4}>
                        <Center py={8}>
                          <Text color="gray.500">No workflows found.</Text>
                        </Center>
                      </Td>
                    </Tr>
                  ) : (
                    <>
                      {filteredData.map((wf, index) => (
                        <Tr key={index}>
                          <Td fontWeight="medium">{wf.workflowName}</Td>
                          <Td isNumeric>{getBadge(wf.pending, "pending")}</Td>
                          <Td isNumeric>{getBadge(wf.approved, "approved")}</Td>
                          <Td isNumeric>{getBadge(wf.rejected, "rejected")}</Td>
                        </Tr>
                      ))}
                      <Tr
                        bg="gray.50"
                        fontWeight="bold"
                        borderTop="2px solid"
                        borderTopColor="gray.200"
                      >
                        <Td>Total</Td>
                        <Td isNumeric>{getBadge(totals.pending, "pending")}</Td>
                        <Td isNumeric>{getBadge(totals.approved, "approved")}</Td>
                        <Td isNumeric>{getBadge(totals.rejected, "rejected")}</Td>
                      </Tr>
                    </>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Second Tab */}
        <TabPanel>
          <Box p={6} bg="gray.50" borderRadius="md">
             <ApprovalTimelineTab />
          </Box>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default WorkUserReports;
